/**
 * De tabs: een indicator die van tab naar tab reist en panelen die meeschuiven.
 *
 * De indicator wordt niet als één blok verplaatst maar op zijn twee randen gestuurd.
 * Daardoor rekt hij onderweg uit tussen tabs van verschillende breedte in plaats van
 * abrupt van maat te wisselen. Hij schiet een paar pixels voorbij zijn doel en zakt dan
 * terug -- geklemd op de padding van de baan, zodat hij er nooit uit steekt. Tegelijk
 * duikt hij even in elkaar en veert terug: de squash die het geheel gewicht geeft.
 *
 * Wrapt de nav over meerdere rijen, dan glijdt de indicator ook tussen die rijen; daarom
 * rekent alles met offsetTop en offsetHeight en niet alleen met de horizontale as.
 *
 * De maten zijn gedempt ten opzichte van het origineel: dit ontwerp houdt een hoek van
 * 4px aan en een rechthoek die stuitert leest zwaarder dan een pil die dat doet.
 *
 * Progressive enhancement: zonder JS staat het eerste paneel gewoon in beeld. Met
 * prefers-reduced-motion wisselt alles direct, zonder beweging.
 *
 * Markup: resources/views/blocks/tabs-block.blade.php.
 * Maten: resources/styles/common/tabs.scss.
 */

import gsap from 'gsap';
import { CustomEase } from 'gsap/CustomEase';

gsap.registerPlugin(CustomEase);

if (!CustomEase.get('osmo')) {
  CustomEase.create('osmo', 'M0,0 C0.625,0.05 0,1 1,1');
}

const config = {
  // De reis naar het doel en de terugkeer uit het overschot.
  travel: 0.35,
  settle: 0.25,

  // De squash van de indicator: kort in elkaar, langer terug met een lichte veer.
  squashDown: 0.15,
  squashBack: 0.45,
  squashScale: 0.86,

  // Hoeveel pixels de indicator voorbij zijn doel mag schieten.
  overshoot: 10,

  // Het inzakken en terugveren van het paneelvlak zelf.
  cardSquash: 0.2,
  cardInflate: 0.45,
};

export default function initTabs() {
  const roots = document.querySelectorAll('[data-tabs]');

  if (!roots.length) {
    return;
  }

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  roots.forEach((root, rootIndex) => {
    const nav = root.querySelector('[data-tabs-nav]');
    const indicator = root.querySelector('[data-tabs-indicator]');
    const ghost = root.querySelector('[data-tabs-ghost]');
    const buttons = nav ? Array.from(nav.querySelectorAll('[data-tabs-button]')) : [];

    // Onder de twee tabs valt er niets te wisselen; dan blijft het bij wat er staat.
    if (!nav || !indicator || buttons.length < 2) {
      return;
    }

    const card = root.querySelector('[data-tabs-card]');
    const panelsWrap = root.querySelector('[data-tabs-panels]');
    const panels = panelsWrap ? Array.from(panelsWrap.querySelectorAll('[data-tabs-panel]')) : [];
    const hasCard = Boolean(card && panelsWrap && panels.length);

    // De koppeling tussen knop en paneel: het script legt de id's en de aria-relatie zelf,
    // zodat de blade geen nummers hoeft bij te houden die toch niet kloppen.
    buttons.forEach((button, i) => {
      const panel = panels[i];

      if (!panel) {
        return;
      }

      if (!button.id) {
        button.id = `tabs-${rootIndex + 1}-tab-${i + 1}`;
      }

      if (!panel.id) {
        panel.id = `tabs-${rootIndex + 1}-panel-${i + 1}`;
      }

      button.setAttribute('aria-controls', panel.id);
      panel.setAttribute('aria-labelledby', button.id);
    });

    let activeIndex = Math.max(0, buttons.findIndex((button) => button.hasAttribute('data-active')));
    let ghostVisible = false;

    // De twee randen van de indicator. Ze worden los getweend, want juist het verschil
    // in hun snelheid is wat hem onderweg laat uitrekken.
    const pos = { left: 0, right: 0 };

    const dur = (value) => (reduceMotion.matches ? 0 : value);

    function syncButtonState() {
      buttons.forEach((button, i) => {
        button.toggleAttribute('data-active', i === activeIndex);
        button.setAttribute('aria-selected', i === activeIndex ? 'true' : 'false');
        button.tabIndex = i === activeIndex ? 0 : -1;
      });
    }

    // offset* en niet getBoundingClientRect(): die eerste is al relatief aan de baan en
    // rekent de transform van de indicator zelf niet mee.
    function rectFor(button) {
      return {
        left: button.offsetLeft,
        right: button.offsetLeft + button.offsetWidth,
        top: button.offsetTop,
        height: button.offsetHeight,
      };
    }

    function render() {
      gsap.set(indicator, { x: pos.left, width: pos.right - pos.left });
    }

    function place(index) {
      const target = rectFor(buttons[index]);

      pos.left = target.left;
      pos.right = target.right;

      gsap.killTweensOf(pos);
      gsap.killTweensOf(indicator);
      gsap.set(indicator, { scaleY: 1, y: target.top, height: target.height });
      render();
    }

    // De panelen liggen op elkaar, dus de wikkel heeft geen eigen hoogte; die krijgt hij
    // van het paneel dat aan staat.
    function setPanelsHeight() {
      if (hasCard) {
        gsap.set(panelsWrap, { height: panels[activeIndex].offsetHeight });
      }
    }

    function swapPanelsInstant() {
      if (!hasCard) {
        return;
      }

      panels.forEach((panel, i) => {
        gsap.killTweensOf(panel);
        gsap.set(panel, { autoAlpha: i === activeIndex ? 1 : 0, x: 0 });
        panel.toggleAttribute('data-active', i === activeIndex);
      });

      gsap.set(card, { scaleX: 1, scaleY: 1, x: 0 });
      setPanelsHeight();
    }

    // Het vlak zakt kort in en helt mee met de looprichting terwijl de inhoud eronder
    // wisselt; daarna veert het terug. Subtieler dan bij een losse kaart, want hier is het
    // vlak de inhoud zelf en niet een doos eromheen.
    function transitionCard(prevIndex, movingRight) {
      if (!hasCard) {
        return;
      }

      const direction = movingRight ? 1 : -1;
      const incoming = panels[activeIndex];
      const outgoing = panels[prevIndex];

      if (!incoming || !outgoing) {
        return;
      }

      // Panelen waar niemand langs komt gaan meteen uit beeld: bij snel doorklikken zou
      // er anders eentje halverwege blijven staan.
      panels.forEach((panel) => {
        if (panel !== incoming && panel !== outgoing) {
          gsap.killTweensOf(panel);
          gsap.set(panel, { autoAlpha: 0, x: 0 });
          panel.removeAttribute('data-active');
        }
      });

      incoming.setAttribute('data-active', '');
      outgoing.removeAttribute('data-active');

      gsap.killTweensOf([card, panelsWrap, incoming, outgoing]);
      gsap.set(card, { transformOrigin: '50% 50%' });

      const targetHeight = incoming.offsetHeight;

      gsap.timeline()
        .to(card, {
          scaleX: 0.98,
          scaleY: 1.015,
          x: direction * 8,
          duration: config.cardSquash,
          ease: 'power2.out',
        }, 0)
        .to(outgoing, {
          x: direction * -35,
          autoAlpha: 0,
          duration: 0.2,
          ease: 'power2.out',
        }, 0)
        .to(panelsWrap, {
          height: targetHeight,
          duration: 0.3,
          ease: 'power2.inOut',
        }, 0.1)
        .fromTo(incoming, {
          x: direction * 35,
          autoAlpha: 0,
        }, {
          x: 0,
          autoAlpha: 1,
          duration: 0.3,
          ease: 'osmo',
        }, config.cardSquash)
        .to(card, {
          scaleX: 1,
          scaleY: 1,
          x: 0,
          duration: config.cardInflate,
          ease: 'back.out(1.6)',
        }, config.cardSquash)
        .set(outgoing, { x: 0 });
    }

    function goTo(index) {
      if (index === activeIndex) {
        return;
      }

      const prevIndex = activeIndex;
      const movingRight = index > activeIndex;

      activeIndex = index;
      syncButtonState();

      if (reduceMotion.matches) {
        place(index);
        swapPanelsInstant();

        return;
      }

      transitionCard(prevIndex, movingRight);

      const target = rectFor(buttons[index]);

      gsap.killTweensOf(pos);
      gsap.killTweensOf(indicator);

      // Ligt het doel op dezelfde x -- bij een nav die wrapt gebeurt dat -- dan bepaalt de
      // volgorde van de tabs de richting, want het verschil in x zegt dan niets.
      const horizontalDirection = target.left === pos.left
        ? (movingRight ? 1 : -1)
        : (target.left > pos.left ? 1 : -1);

      const distance = Math.abs(target.left - pos.left);

      // Verder weg mag verder doorschieten, maar nooit voorbij de rand van de baan: het
      // overschot leeft in de padding en niet daarbuiten.
      let overshoot = gsap.utils.clamp(4, config.overshoot, distance * 0.08) * horizontalDirection;

      if (horizontalDirection > 0) {
        overshoot = Math.min(overshoot, nav.clientWidth - target.right);
      } else {
        overshoot = Math.max(overshoot, -target.left);
      }

      gsap.set(indicator, { transformOrigin: '50% 50%' });

      // De verticale sprong -- alleen zichtbaar bij een nav die wrapt -- loopt in één keer
      // door en doet niet mee aan het overschot.
      gsap.to(indicator, {
        y: target.top,
        height: target.height,
        duration: config.travel,
        ease: 'osmo',
      });

      gsap.timeline()
        .to(pos, {
          left: target.left + overshoot,
          right: target.right + overshoot,
          duration: config.travel,
          ease: 'osmo',
          onUpdate: render,
        })
        .to(pos, {
          left: target.left,
          right: target.right,
          duration: config.settle,
          ease: 'power2.inOut',
          onUpdate: render,
        });

      gsap.timeline()
        .to(indicator, {
          scaleY: config.squashScale,
          duration: config.squashDown,
          ease: 'power2.out',
        })
        .to(indicator, {
          scaleY: 1,
          duration: config.squashBack,
          ease: 'back.out(1.8)',
        });
    }

    // De waas onder de cursor. Bij de eerste hover verschijnt hij op zijn plek in plaats
    // van vanaf een oude positie aan te komen glijden.
    function moveGhost(button) {
      if (!ghost) {
        return;
      }

      const target = rectFor(button);

      if (!ghostVisible) {
        ghostVisible = true;

        gsap.killTweensOf(ghost);
        gsap.set(ghost, {
          x: target.left,
          y: target.top,
          width: target.right - target.left,
          height: target.height,
        });
        gsap.to(ghost, { autoAlpha: 1, duration: dur(0.25), ease: 'power2.out' });

        return;
      }

      gsap.to(ghost, {
        x: target.left,
        y: target.top,
        width: target.right - target.left,
        height: target.height,
        autoAlpha: 1,
        duration: dur(0.3),
        ease: 'osmo',
      });
    }

    function hideGhost() {
      if (!ghost) {
        return;
      }

      ghostVisible = false;
      gsap.to(ghost, { autoAlpha: 0, duration: dur(0.2), ease: 'power1.out' });
    }

    // Na een resize klopt geen enkele gemeten maat meer, dus die worden opnieuw gezet in
    // plaats van getweend. data-wrapped vertelt de stijl dat de baan meerdere rijen heeft.
    function refreshLayout() {
      const rows = new Set(buttons.map((button) => button.offsetTop));

      nav.toggleAttribute('data-wrapped', rows.size > 1);
      place(activeIndex);
      setPanelsHeight();
    }

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => goTo(index));
      button.addEventListener('mouseenter', () => moveGhost(button));
    });

    nav.addEventListener('mouseleave', hideGhost);

    nav.addEventListener('keydown', (event) => {
      let next = null;

      if (event.key === 'ArrowRight') {
        next = Math.min(buttons.length - 1, activeIndex + 1);
      }

      if (event.key === 'ArrowLeft') {
        next = Math.max(0, activeIndex - 1);
      }

      if (event.key === 'Home') {
        next = 0;
      }

      if (event.key === 'End') {
        next = buttons.length - 1;
      }

      if (next === null) {
        return;
      }

      event.preventDefault();

      if (next !== activeIndex) {
        goTo(next);
        buttons[next].focus();
      }
    });

    window.addEventListener('resize', refreshLayout);

    // Het lettertype komt na de eerste meting binnen en verandert de breedte van elke tab.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(refreshLayout);
    }

    if (ghost) {
      gsap.set(ghost, { autoAlpha: 0 });
    }

    panels.forEach((panel, i) => gsap.set(panel, { autoAlpha: i === activeIndex ? 1 : 0 }));

    syncButtonState();
    refreshLayout();
  });
}
